/**
 * What an empty local store means at start-up, and what the reader sees while
 * it is being put right.
 *
 * An empty IndexedDB under a known account is almost never an empty vault. It
 * is a browser that evicted the cache, a fresh device, or a cleared site. Every
 * one of those looks like data loss if the shell opens onto "Nothing written
 * yet", so a hosted account with nothing local goes straight into a resync and
 * says so. Offline there is nothing to resync from, and the honest screen is
 * the one that says the cached copy is gone, not the one that says the vault
 * is empty.
 */

export type StartupPhase = "ready" | "restoring" | "restored" | "unreachable";

export interface StartupState {
  /** Notes found in the local store when the vault opened. */
  readonly noteCount: number;
  /** A hosted account, as opposed to a purely local vault. */
  readonly hosted: boolean;
  /** Whether the server validated the session on this launch; false offline. */
  readonly validated: boolean;
}

/** Whether this launch needs to restore before the vault can be trusted. */
export function startupPhase(state: StartupState): StartupPhase {
  if (!state.hosted || state.noteCount > 0) return "ready";
  return state.validated ? "restoring" : "unreachable";
}

export interface StartupPorts {
  /** Runs a full sync; resolves to whether it succeeded. */
  readonly sync: () => Promise<boolean>;
  /** Shows the current phase to the reader. */
  readonly show: (phase: StartupPhase) => void;
}

/**
 * Restores the vault when the local store is empty, showing the restoring
 * state throughout, and reports the phase the vault ended in.
 */
export async function restoreOnStartup(state: StartupState, ports: StartupPorts): Promise<StartupPhase> {
  const phase = startupPhase(state);
  ports.show(phase);
  if (phase !== "restoring") return phase;
  const synced = await ports.sync();
  const ended: StartupPhase = synced ? "restored" : "unreachable";
  ports.show(ended);
  return ended;
}

export interface WelcomeCopy {
  readonly title: string;
  readonly copy: string;
}

/**
 * The welcome text for an empty list in each phase. `undefined` hands the
 * decision back to the ordinary empty-vault copy.
 */
export function startupWelcome(phase: StartupPhase): WelcomeCopy | undefined {
  switch (phase) {
    case "restoring":
      return { title: "Restoring your vault…", copy: "This browser had no local copy, so your notes are being fetched from the hosted vault." };
    case "unreachable":
      return { title: "Your notes are on the hosted vault.", copy: "This browser has no local copy and cannot reach the hosted vault right now. Reconnect and sync to restore them." };
    default:
      return undefined;
  }
}

export interface ConnectionOverride {
  readonly label: string;
  /** The class the connection line takes, in place of the sync state's own. */
  readonly state: "restoring" | "offline";
}

/**
 * What the connection line says while start-up outranks ordinary sync status:
 * a restore in progress, or a vault opened from the cache because the server
 * could not be reached. Once neither applies the sync state speaks for itself.
 */
export function connectionOverride(phase: StartupPhase, offlineStart: boolean): ConnectionOverride | undefined {
  if (phase === "restoring") return { label: "Restoring from hosted vault", state: "restoring" };
  if (offlineStart) return { label: "Offline · reading the cached vault", state: "offline" };
  return undefined;
}
